/** @jsx jsx */
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { Paper, Typography, useTheme } from '@material-ui/core';

import iconError from 'assets/icons/iconError.svg';
import Button from 'components/Button';
import { ICustomTheme } from 'interfaces';

const ResponsiveContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});

const WarningText = styled(Typography)(
  ({ theme, isMobile }: { theme: ICustomTheme; isMobile: boolean }) => ({
    marginTop: theme.spacing(3),
    color: theme.colors.paletteFromBO.main,
    fontSize: isMobile ? 25 : 48,
    fontWeight: theme.typography.fontWeightBold,
  }),
);

const ErrorText = styled(Typography)(
  ({ theme, isMobile }: { theme: ICustomTheme; isMobile: boolean }) => ({
    color: theme.colors.grey.dark,
    fontSize: isMobile ? 18 : 28,
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightBold,
  }),
);

const ActionText = styled(Typography)(
  ({ theme, isMobile }: { theme: ICustomTheme; isMobile: boolean }) => ({
    color: theme.colors.grey.dark,
    fontSize: isMobile ? theme.typography.body2.fontSize : 22,
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightMedium,
  }),
);

export default function ErrorRedirectionView({
  errorText,
  actionText,
  buttonText,
  redirection,
  isMobile = false,
}: {
  errorText?: string;
  actionText?: string;
  buttonText: string;
  redirection: string;
  isMobile?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();

  const [Container, containerProps] = isMobile
    ? ['section', { css: { padding: theme.spacing(2) } }]
    : [
        Paper,
        {
          component: 'section' as 'section',
          css: { padding: theme.spacing(6) },
          elevation: 2,
        },
      ];

  return (
    <Container
      {...containerProps}
      css={{ width: '100%', ...containerProps.css }}
    >
      <ResponsiveContainer>
        <img src={iconError} alt="error-icon" />
        <WarningText isMobile={isMobile}>
          {t('challenges.declaration.history.surprised_text')}
        </WarningText>
        {errorText && <ErrorText isMobile={isMobile}>{errorText}</ErrorText>}
        {actionText && (
          <ActionText isMobile={isMobile}>{actionText}</ActionText>
        )}
        <Button
          component={RouterLink}
          css={{
            marginTop: theme.spacing(3),
          }}
          color="primary"
          to={redirection}
        >
          {buttonText}
        </Button>
      </ResponsiveContainer>
    </Container>
  );
}
